import "./StepIndicator.css";

import { Button, Form, FormGroup, Input } from "reactstrap";
import {
  EXPERIMENT_STATUS,
  FLOW_STATUS_MESSAGES,
  PLOTMAP_STATUS,
} from "../../constants";
import { ProgressBar, Step } from "react-step-progress-bar";
import { useDispatch, useSelector } from "react-redux";
import { useMemo, useState } from "react";

import { FieldMapSVG } from "../../static/svg/FieldMapSVG";
import { InlineSpinner } from "../../components/InlineSpinner";
import { PlotSVG } from "../../static/svg/PlotSVG";
import PropTypes from "prop-types";
import { updateSitePlotmapStatus } from "../../actions/sites";

const MultiStepBarIndicator = ({ plotmapStatus, experimentStatus }) => {
  const { currentStep, stepPercentage } = useMemo(() => {
    const stepStatusMap = {
      0: [EXPERIMENT_STATUS.NOT_STARTED],
      1: [EXPERIMENT_STATUS.DRAFT],
      2: [EXPERIMENT_STATUS.COMPLETED, EXPERIMENT_STATUS.SYNCHRONIZED],
      3: [
        PLOTMAP_STATUS.WAITING_FOR_VALIDATION,
        PLOTMAP_STATUS.CHANGES_REQUESTED,
      ],
      4: [PLOTMAP_STATUS.VALIDATED],
    };

    const entries = Object.entries(stepStatusMap);
    let step = -1;

    if (plotmapStatus >= PLOTMAP_STATUS.WAITING_FOR_VALIDATION) {
      const plotmapStepIndex = entries.findIndex(
        ([step, statuses]) => step >= 3 && statuses.includes(plotmapStatus)
      );
      if (plotmapStepIndex !== -1) step = Number(entries[plotmapStepIndex][0]);
    } else {
      const experimentStepIndex = entries.findIndex(
        ([step, statuses]) => step <= 2 && statuses.includes(experimentStatus)
      );
      if (experimentStepIndex !== -1)
        step = Number(entries[experimentStepIndex][0]);
    }

    const totalSteps = entries.length - 1;
    const percentage =
      step >= 0 ? 10 + Math.round((step / totalSteps) * (100 - 5)) : 0;

    return { currentStep: step, stepPercentage: percentage };
  }, [plotmapStatus, experimentStatus]);

  return (
    <div
      className={`progress-wrapper ${
        plotmapStatus === 1 || plotmapStatus === 3 ? "changes-requested" : ""
      }`}
    >
      <ProgressBar percent={stepPercentage}>
        <Step>
          {({ accomplished, index }) => (
            <div className="step">
              <div
                className={`indexed-step ${
                  accomplished && experimentStatus >= 0 ? "accomplished" : ""
                }
                ${index === currentStep ? "current" : ""}`}
              >
                {accomplished && experimentStatus >= 0 ? (
                  <i className="fa-solid fa-check" aria-hidden="true" />
                ) : (
                  <FieldMapSVG width="14px" height="14px" />
                )}
              </div>
              <span
                className={`step-label ${
                  index === currentStep ? "step-current" : ""
                }`}
              >
                Field map not started
              </span>
            </div>
          )}
        </Step>
        <Step>
          {({ accomplished, index }) => (
            <div className="step">
              <div
                className={`indexed-step ${
                  accomplished && experimentStatus >= 0 ? "accomplished" : ""
                }
                ${index === currentStep ? "current" : ""}`}
              >
                {accomplished && experimentStatus >= 0 ? (
                  <i className="fa-solid fa-check" aria-hidden="true" />
                ) : (
                  <FieldMapSVG width="14px" height="14px" />
                )}
              </div>
              <span
                className={`step-label ${
                  index === currentStep ? "step-current" : ""
                }`}
              >
                Field map draft
              </span>
            </div>
          )}
        </Step>
        <Step>
          {({ accomplished, index }) => (
            <div className="step">
              <div
                className={`indexed-step ${
                  accomplished && experimentStatus >= 0 ? "accomplished" : ""
                }
                ${index === currentStep ? "current" : ""}`}
              >
                {accomplished && experimentStatus >= 0 ? (
                  <i className="fa-solid fa-check" aria-hidden="true" />
                ) : (
                  <FieldMapSVG width="14px" height="14px" />
                )}
              </div>
              <span
                className={` step-label ${
                  index === currentStep ? "step-current" : ""
                }`}
              >
                Field map Completed
              </span>
            </div>
          )}
        </Step>
        <Step>
          {({ accomplished, index }) => (
            <div className="step">
              {plotmapStatus === 3 ? (
                <>
                  <div
                    className={`indexed-step warning   ${
                      index === currentStep ? "current" : ""
                    }`}
                  >
                    {accomplished ? (
                      <i
                        className="fa-solid fa-exclamation"
                        aria-hidden="true"
                      />
                    ) : (
                      <PlotSVG width="12px" height="12px" />
                    )}
                  </div>
                  <span
                    className={` step-label ${
                      index === currentStep ? "step-current" : ""
                    }`}
                  >
                    Plot map changes requested
                  </span>
                </>
              ) : (
                <>
                  <div
                    className={`indexed-step  ${
                      accomplished ? "accomplished" : ""
                    }
                      ${index === currentStep ? "current warning" : ""}`}
                  >
                    {accomplished ? (
                      plotmapStatus === 1 ? (
                        <i className="fa-regular fa-clock" aria-hidden="true" />
                      ) : plotmapStatus > 1 ? (
                        <i className="fa-solid fa-check" aria-hidden="true" />
                      ) : (
                        <PlotSVG width="12px" height="12px" />
                      )
                    ) : (
                      <PlotSVG width="12px" height="12px" />
                    )}
                  </div>
                  <span
                    className={`step-label ${
                      index === currentStep ? "step-current" : ""
                    }`}
                  >
                    Plot map waiting for validation
                  </span>
                </>
              )}
            </div>
          )}
        </Step>
        <Step>
          {({ accomplished, index }) => (
            <div className="step">
              <div
                className={`indexed-step ${accomplished ? "accomplished" : ""}
                  ${index === currentStep ? "current" : ""}`}
              >
                {accomplished ? (
                  <i className="fa-solid fa-check" aria-hidden="true" />
                ) : (
                  <PlotSVG width="12px" height="12px" />
                )}
              </div>
              <span
                className={`step-label ${
                  index === currentStep ? "step-current" : ""
                }`}
              >
                Plot map validated
              </span>
            </div>
          )}
        </Step>
      </ProgressBar>
    </div>
  );
};

const StepIndicator = ({ plotmapStatus, experimentStatus, siteId, user }) => {
  const [message, setMessage] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(null);
  const dispatch = useDispatch();
  const trialDate = useSelector(({ resultMap }) => resultMap.trial_date);

  const flowStatusMessage = useMemo(() => {
    if (plotmapStatus === PLOTMAP_STATUS.VALIDATED)
      return FLOW_STATUS_MESSAGES.PLOTMAP_VALIDATED;
    if (plotmapStatus === PLOTMAP_STATUS.WAITING_FOR_VALIDATION)
      return FLOW_STATUS_MESSAGES.PLOTMAP_WAITING_VALIDATION;
    if (plotmapStatus === PLOTMAP_STATUS.CHANGES_REQUESTED)
      return FLOW_STATUS_MESSAGES.PLOTMAP_CHANGES_REQUESTED;
    if (experimentStatus === EXPERIMENT_STATUS.NOT_REQUIRED)
      return FLOW_STATUS_MESSAGES.EXPERIMENT_NOT_REQUIRED;
    if (experimentStatus >= EXPERIMENT_STATUS.COMPLETED)
      return FLOW_STATUS_MESSAGES.EXPERIMENT_SYNCHRONIZED;

    return null;
  }, [plotmapStatus, experimentStatus]);
  return (
    <div className="step-indicator">
      <div className="multi-progress-step">
        <MultiStepBarIndicator
          plotmapStatus={plotmapStatus}
          experimentStatus={experimentStatus}
        />
      </div>
      {flowStatusMessage && (
        <div
          className={`mt-2 w-100 d-flex align-items-center gap-1 justify-content-end`}
          style={{ color: flowStatusMessage.color }}
        >
          <span>
            <i className={flowStatusMessage.icon} />
          </span>
          {flowStatusMessage.text}
        </div>
      )}
      {(plotmapStatus === PLOTMAP_STATUS.WAITING_FOR_VALIDATION ||
        plotmapStatus === PLOTMAP_STATUS.CHANGES_REQUESTED) && (
        <>
          {!isOpen ? (
            <div className="w-100 d-flex flex-row gap-1 align-items-center justify-content-end mt-2 me-2">
              <Button
                className="hiphen-changes-button text-nowrap"
                size="sm"
                disabled={loading}
                onClick={() => setOpen(true)}
              >
                Request Changes
              </Button>

              <Button
                color="success"
                className="text-nowrap"
                size="sm"
                disabled={loading}
                onClick={async () => {
                  setLoading("validate");
                  try {
                    await dispatch(
                      updateSitePlotmapStatus({
                        id: siteId,
                        plotmap_status: PLOTMAP_STATUS.VALIDATED,
                        acquisition_date: trialDate,
                      })
                    );
                  } finally {
                    setLoading(null);
                  }
                }}
              >
                {loading === "validate" ? (
                  <InlineSpinner />
                ) : (
                  "Validate Plot Map"
                )}
              </Button>
            </div>
          ) : (
            <div className={`transition-container ${isOpen ? "open" : ""}`}>
              <Form>
                <FormGroup>
                  <Input
                    type="textarea"
                    className="input-text-area"
                    placeholder="add a comment ..."
                    onChange={(e) => setMessage(e.target.value)}
                    value={message}
                  />
                </FormGroup>
              </Form>

              <div className="d-flex flex-row gap-1 justify-content-start mt-2">
                <Button
                  className="hiphen-changes-button text-nowrap"
                  size="sm"
                  onClick={async () => {
                    setLoading("changes");
                    try {
                      await dispatch(
                        updateSitePlotmapStatus({
                          id: siteId,
                          plotmap_status: PLOTMAP_STATUS.CHANGES_REQUESTED,
                          acquisition_date: trialDate,
                          ...(message ? { message } : {}),
                        })
                      );
                      setOpen(false);
                      setMessage("");
                    } finally {
                      setLoading(null);
                    }
                  }}
                  disabled={loading}
                >
                  {loading === "changes" ? (
                    <InlineSpinner />
                  ) : (
                    "Request changes"
                  )}
                </Button>

                <Button
                  size="sm"
                  color="secondary"
                  onClick={() => {
                    setOpen(false);
                    setMessage("");
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

StepIndicator.propTypes = {
  plotmapStatus: PropTypes.number.isRequired,
  experimentStatus: PropTypes.number.isRequired,
  siteId: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
};
export default StepIndicator;
